import { useCallback } from 'react';
import { DeepMap, FieldError, useFieldArray, useFormContext, UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { OPTIONS } from 'adapter/in/common/widget/options';
import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';
import { Fields, FormFieldData, FormFields, TextInputFieldData, Value } from 'adapter/in/common/form/types';

export type RemoveElementRHF = number | number[] | undefined;

type ArrayRangeError = {
    ranges: {
        from?: {
            message: string;
        };
        to?: {
            message: string;
        };
    }[];
};

export const getRangeErrors = (
    errors: DeepMap<Fields<FormFieldData>, FieldError>,
    index: number,
    fieldName: string
): string | undefined => {
    if (errors.ranges !== undefined) {
        const errorRange = (errors as ArrayRangeError).ranges[index];
        switch (fieldName) {
            case 'from':
                return errorRange?.from?.message;
            case 'to':
                return errorRange?.to?.message;
            default:
                return undefined;
        }
    }
    return undefined;
};

type ArrayChartLimitError = {
    checkpoints: {
        value?: {
            message: string;
            ref: {
                name: string;
                value: TextInputFieldData;
            };
            type: string;
        };
    }[];
};

export const getChartLimitErrors = (errors: DeepMap<Fields<FormFieldData>, FieldError>, index: number): string | undefined =>
    errors.checkpoints ? (errors as ArrayChartLimitError).checkpoints[index]?.value?.message : undefined;

export const getErrorForArrayField = (
    errors: DeepMap<Fields<FormFieldData>, FieldError>,
    name: string,
    index: number,
    fieldName: string
): string | undefined => {
    switch (name) {
        case OPTIONS.RANGES:
            return getRangeErrors(errors, index, fieldName);
        case OPTIONS.CHECKPOINTS:
            return getChartLimitErrors(errors, index);
        default:
            return undefined;
    }
};

const useArrayField = <TValue extends Value>(
    name: string,
    handleAdd?: (element: TValue) => void,
    handleRemove?: (index?: RemoveElementRHF) => void
): {
    errors: DeepMap<Fields<TValue>, FieldError>;
    fields: TValue[];
    getValues: UseFormGetValues<Fields<TValue>>;
    handleAddElement: (element: TValue) => void;
    handleRemoveElement: (index?: RemoveElementRHF) => void;
    setValue: UseFormSetValue<Fields<TValue>>;
} => {
    type ItemArrayFieldData = TValue & { id: string };
    const { watch } = useFormContext<FormFields>();
    const {
        getValues,
        setValue,
        formState: { errors },
    } = useFormContext<Fields<TValue>>();
    const { append, fields, remove } = useFieldArray({ name });
    const { setHasChange: setWidgetMenuHasChange } = dispatchFormChangesView();

    watch(name);

    const handleAddElement = useCallback((element: TValue) => {
        append(element);
        if (handleAdd) handleAdd(element);
        setWidgetMenuHasChange(true);
    }, []);

    const handleRemoveElement = useCallback((index?: RemoveElementRHF) => {
        remove(index);
        if (handleRemove) handleRemove(index);
        setWidgetMenuHasChange(true);
    }, []);

    return {
        errors,
        fields: fields as ItemArrayFieldData[],
        getValues,
        handleAddElement,
        handleRemoveElement,
        setValue,
    };
};

export default useArrayField;
